import axios from 'axios';

import { constants } from '../modules/Constants';
import {SERVER_STATUS} from '../modules/Enumeration';

// API URL from constants
const API_URL = constants.API_URL;

const DEFAULT_TIMEOUT_MS = 600000;
const DEFAULT_RETRY_COUNT = 2;
const DEFAULT_RETRY_BASE_DELAY_MS = 300;

const serializeParams = (params = {}) => {
  const parts = [];

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) return;

    if (Array.isArray(value)) {
      value.forEach((v) => {
        if (v === undefined || v === null) return;
        parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(v))}`);
      });
      return;
    }

    if (typeof value === 'object') {
      parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(JSON.stringify(value))}`);
      return;
    }

    parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
  });

  return parts.join('&');
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const shouldRetryRequest = (method, error, attempt, maxRetries) => {
  if (attempt >= maxRetries) return false;
  const upperMethod = String(method || '').toUpperCase();
  if (upperMethod !== 'GET') return false;

  if (error?.isNetworkError) return true;
  const status = error?.status ?? error?.response?.status;
  if (typeof status === 'number' && status >= 500) return true;

  const code = error?.code;
  if (code === 'ECONNABORTED') return true;

  return false;
};

// Create an axios instance with default config
const axiosInstance = axios.create({
  baseURL: API_URL,
  timeout: DEFAULT_TIMEOUT_MS,
  paramsSerializer: {
    serialize: serializeParams,
  },
});

/**
 * Service for handling API requests using Axios
 */
class APIService {
  constructor() {

    this.statusCallback = null;
    this.isRefreshing = false;
    this.refreshSubscribers = [];
    this.tokenRefreshBuffer = 5 * 60 * 1000;
    this.refreshTokenInvalid = false;
    this.authErrorNotified = false;

    this.inFlightGetRequests = new Map();
    this.abortControllers = new Map();

    // Add request interceptor to add auth token to requests
    axiosInstance.interceptors.request.use(
      async (config) => {
        const publicAuthEndpoints = [
          '/auth/login',
          '/auth/register',
          '/auth/signup',
          '/auth/social-login',
          '/auth/verify-email',
          '/auth/confirm-email',
          '/auth/username-exists',
          '/auth/verify-2fa-login',
          '/auth/change-password',
        ];

        // Skip token refresh logic for refresh-token endpoint and public auth endpoints
        if (
          config.url &&
          (config.url.includes('/auth/refresh-token') ||
            publicAuthEndpoints.some((endpoint) => config.url.includes(endpoint)))
        ) {
          return config;
        }

        const token = localStorage.getItem('@auth_token');
        const tokenExpiry = localStorage.getItem('@token_expiry');
        const refreshToken = localStorage.getItem('@refresh_token');

        // Check if token is about to expire (within 5 minutes)
        if (token && tokenExpiry && refreshToken) {
          const expiryDate = new Date(tokenExpiry);
          const now = new Date();
          const timeUntilExpiry = expiryDate.getTime() - now.getTime();

          // If token expires in less than 5 minutes, refresh it proactively
          if (timeUntilExpiry < this.tokenRefreshBuffer && timeUntilExpiry > 0) {
            if (!this.isRefreshing) {
              try {
                await this.proactiveTokenRefresh(refreshToken);
              } catch (error) {
                console.warn('Proactive token refresh failed:', error);
              }
            }
          }
        }

        // Add current token to request
        const currentToken = localStorage.getItem('@auth_token');
        if (currentToken) {
          config.headers.Authorization = `Bearer ${currentToken}`;
        }
        return config;
      },
      (error) => {
        this.callStatusCallback(SERVER_STATUS.ERROR);
        return Promise.reject(error);
      }
    );

    // Add response interceptor to handle errors and automatic token refresh
    axiosInstance.interceptors.response.use(
      (response) => {
        this.callStatusCallback(SERVER_STATUS.OK);
        return response;
      },
      async (error) => {
        const originalRequest = error.config;

        const publicAuthEndpoints = [
          '/auth/login',
          '/auth/register',
          '/auth/signup',
          '/auth/social-login',
          '/auth/verify-email',
          '/auth/confirm-email',
          '/auth/username-exists',
          '/auth/verify-2fa-login',
          '/auth/change-password',
        ];

        const isPublicAuthRequest =
          originalRequest?.url &&
          publicAuthEndpoints.some((endpoint) => originalRequest.url.includes(endpoint));

        if (error.response) {
          if (error.response.status === 401 && this.refreshTokenInvalid && !isPublicAuthRequest) {
            this.callStatusCallback(SERVER_STATUS.ERROR_AUTH, {
              status: 401,
              message: 'Session expired',
              reason: 'refresh_token_invalid',
            });
            return Promise.reject({
              status: 401,
              message: error.response.data?.message || 'Session expired',
              data: error.response.data,
            });
          }

          // Handle 401 Unauthorized with automatic token refresh
          if (
            error.response.status === 401 &&
            originalRequest &&
            !originalRequest._retry &&
            !originalRequest.url.includes('/auth/refresh-token') &&
            !isPublicAuthRequest
          ) {
            originalRequest._retry = true;

            const refreshToken = localStorage.getItem('@refresh_token');

            if (!refreshToken) {
              this.callStatusCallback(SERVER_STATUS.ERROR_AUTH, {
                status: 401,
                message: 'No refresh token',
                reason: 'no_refresh_token',
              });
              return Promise.reject({
                status: error.response.status,
                message: error.response.data?.message || 'API request failed',
                data: error.response.data,
              });
            }

            // If a refresh is already in progress, queue the request
            if (this.isRefreshing) {
              return new Promise((resolve, reject) => {
                this.refreshSubscribers.push((token) => {
                  if (!token) {
                    reject({
                      status: 401,
                      message: 'Unable to refresh token',
                      data: null,
                    });
                    return;
                  }
                  originalRequest.headers = originalRequest.headers || {};
                  originalRequest.headers.Authorization = `Bearer ${token}`;
                  resolve(axiosInstance(originalRequest));
                });
              });
            }

            this.isRefreshing = true;

            try {
              const refreshResponse = await axiosInstance.post(
                '/auth/refresh-token',
                {},
                {
                  headers: {
                    Authorization: `Bearer ${refreshToken}`,
                  },
                }
              );

              const refreshData = refreshResponse.data;
              const newToken = refreshData?.data?.accessToken ?? refreshData?.data?.token;
              const newRefreshToken = refreshData?.data?.refreshToken || refreshToken;

              if (!newToken) {
                throw new Error('No token returned from refresh endpoint');
              }

              // Parse JWT to get expiry
              const tokenExpiry = this.parseTokenExpiry(newToken);

              // Persist new tokens
              localStorage.setItem('@auth_token', newToken);
              localStorage.setItem('@refresh_token', newRefreshToken);
              if (tokenExpiry) {
                localStorage.setItem('@token_expiry', tokenExpiry.toISOString());
              }
              this.setAuthToken(newToken);

              // Notify queued requests
              this.refreshSubscribers.forEach((callback) => callback(newToken));
              this.refreshSubscribers = [];
              this.isRefreshing = false;

              // Retry original request with new token
              originalRequest.headers = originalRequest.headers || {};
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              return axiosInstance(originalRequest);
            } catch (refreshError) {
              this.isRefreshing = false;
              this.refreshSubscribers.forEach((callback) => callback(null));
              this.refreshSubscribers = [];

              const refreshStatus = refreshError.response?.status;
              // Clear tokens only if refresh token is truly invalid/expired
              if (refreshStatus === 401 || refreshStatus === 403) {
                localStorage.removeItem('@auth_token');
                localStorage.removeItem('@refresh_token');
                localStorage.removeItem('@token_expiry');
                this.refreshTokenInvalid = true;
              }

              if (!this.authErrorNotified) {
                this.authErrorNotified = true;
                this.callStatusCallback(SERVER_STATUS.ERROR_AUTH, {
                  status: refreshStatus || 401,
                  message: refreshError.response?.data?.message || 'Token refresh failed',
                  reason: 'refresh_failed',
                });
              }

              const status = refreshError.response?.status || 401;
              const data = refreshError.response?.data;
              return Promise.reject({
                status,
                message: data?.message || 'Token refresh failed',
                data,
              });
            }
          }

          const nonRetryStatus = error.response.status;
          const isAuthError = nonRetryStatus === 401;
          this.callStatusCallback(isAuthError ? SERVER_STATUS.ERROR_AUTH : SERVER_STATUS.ERROR, {
            status: nonRetryStatus,
            message: error.response.data?.message || 'API request failed',
            reason: isAuthError ? 'unauthorized' : 'http_error',
          });
          // The request was made and the server responded with a status code
          // that falls out of the range of 2xx
          return Promise.reject({
            status: error.response.status,
            message: error.response.data?.message || 'API request failed',
            data: error.response.data,
          });
        } else if (error.request) {
          this.callStatusCallback(SERVER_STATUS.DOWN);
          // The request was made but no response was received
          const networkError = {
            isNetworkError: true,
            status: 0,
            message: 'Network connection error. Please check your internet connection and API configuration.',
            originalError: error,
          };
          return Promise.reject(networkError);
        } else {
          this.callStatusCallback(SERVER_STATUS.ERROR);
          // Something happened in setting up the request that triggered an Error
          return Promise.reject(error);
        }
      }
    );
  }

  callStatusCallback(status, details = null) {
    if (this.statusCallback) {
      this.statusCallback(status, details);
    }
  }

  setStatusCallback(callback) {
    this.statusCallback = callback;
  }

  clearAuthFailureState() {
    this.refreshTokenInvalid = false;
    this.authErrorNotified = false;
  }

  /**
   * Make a GET request to the API
   * @param {string} endpoint - API endpoint
   * @param {Object} options - Additional axios options
   * @returns {Promise<any>} - Response data
   */
  async get(endpoint, options = {}) {
    return this.request(endpoint, 'GET', null, options);
  }

  /**
   * Make a POST request to the API
   * @param {string} endpoint - API endpoint
   * @param {Object} data - Request body data
   * @param {Object} options - Additional axios options
   * @returns {Promise<any>} - Response data
   */
  async post(endpoint, data, options = {}) {
    return this.request(endpoint, 'POST', data, options);
  }

  /**
   * Make a PUT request to the API
   * @param {string} endpoint - API endpoint
   * @param {Object} data - Request body data
   * @param {Object} options - Additional axios options
   * @returns {Promise<any>} - Response data
   */
  async put(endpoint, data, options = {}) {
    return this.request(endpoint, 'PUT', data, options);
  }

  /**
   * Make a PATCH request to the API
   * @param {string} endpoint - API endpoint
   * @param {Object} data - Request body data
   * @param {Object} options - Additional axios options
   * @returns {Promise<any>} - Response data
   */
  async patch(endpoint, data, options = {}) {
    return this.request(endpoint, 'PATCH', data, options);
  }

  /**
   * Make a DELETE request to the API
   * @param {string} endpoint - API endpoint
   * @param {Object} options - Additional axios options
   * @returns {Promise<any>} - Response data
   */
  async delete(endpoint, options = {}) {
    return this.request(endpoint, 'DELETE', null, options);
  }

  /**
   * Make a request to the API
   * @param {string} endpoint - API endpoint
   * @param {string} method - HTTP method
   * @param {Object} data - Request body data
   * @param {Object} options - Additional axios options
   * @returns {Promise<any>} - Response data
   */
  async request(endpoint, method, data = null, options = {}) {
    try {
      // Check if API_URL is defined
      if (!API_URL) {
        throw new Error('API_URL is not defined. Check environment configuration.');
      }

      // Build request config
      const config = {
        method: method.toLowerCase(),
        url: endpoint,
        data: method !== 'GET' ? data : undefined,
        params: method === 'GET' ? data : undefined,
        ...options,
        headers: {
          ...(options.headers || {})
        }
      };

      // Don't delete data if it's FormData or has content
      if (config.data === null || 
          !config.data || 
          (typeof config.data === 'object' && 
           !(config.data instanceof FormData) && 
           Object.keys(config.data).length === 0)) {
        delete config.data;
      }

      // Set Content-Type header if not already set and data is not FormData
      if (!(config.data instanceof FormData) && !config.headers['Content-Type']) {
        config.headers['Content-Type'] = 'application/json';

      } else {
        if (config.data instanceof FormData) {
          // config.headers['Content-Type'] = 'multipart/form-data';
        }
      }

      // Add token from options if provided
      if (options.token) {
        config.headers.Authorization = `Bearer ${options.token}`;
      }

      const upperMethod = String(method || '').toUpperCase();
      const requestKey =
        upperMethod === 'GET'
          ? `${upperMethod}:${endpoint}?${serializeParams(config.params || {})}`
          : null;

      if (requestKey && this.inFlightGetRequests.has(requestKey)) {
        return this.inFlightGetRequests.get(requestKey);
      }

      const controller = !config.signal ? new AbortController() : null;
      if (controller) {
        config.signal = controller.signal;
      }
      if (requestKey && controller) {
        this.abortControllers.set(requestKey, controller);
      }

      const execute = async () => {
        const maxRetries = typeof options.retry === 'number' ? options.retry : DEFAULT_RETRY_COUNT;
        const baseDelay =
          typeof options.retryDelayMs === 'number' ? options.retryDelayMs : DEFAULT_RETRY_BASE_DELAY_MS;

        let attempt = 0;
        // eslint-disable-next-line no-constant-condition
        while (true) {
          try {
            const response = await axiosInstance.request(config);
            return response.data;
          } catch (err) {
            if (!shouldRetryRequest(upperMethod, err, attempt, maxRetries)) {
              throw err;
            }
            const delay = baseDelay * Math.pow(2, attempt);
            attempt += 1;
            await sleep(delay);
          }
        }
      };

      const promise = execute().finally(() => {
        if (requestKey) {
          this.inFlightGetRequests.delete(requestKey);
          this.abortControllers.delete(requestKey);
        }
      });

      if (requestKey) {
        this.inFlightGetRequests.set(requestKey, promise);
      }

      const responseData = await promise;
      this.callStatusCallback(SERVER_STATUS.OK);
      return responseData;
      
    } catch (error) {

      if (process.env.NODE_ENV === 'development') {
        console.log("ERROR", error);
      }

      if (error.status === 503) {
        console.log("CALLED ")
        this.callStatusCallback(SERVER_STATUS.DOWN);
        return;
      }

      // Check if this is a URL-related error
      if (error.message && error.message.includes('API_URL is not defined')) {
        console.error('API_URL configuration error:', error.message);
        throw error;
      }

      // Check if this is a 401 Unauthorized error
      if (error.response && error.response.status === 401) {
        console.error('API request unauthorized:', error.message);
        // Re-throw the error (handled by the interceptor)

        try {
          // getAuthContextValue().signOut();
        } catch (error) {
          console.error('Error signing out:', error.message);
        }

        return;
      }
      
      // Re-throw the error (handled by the interceptor)
      throw error;
    }
  }

  /**
   * Make a request with raw JSON data in the request body
   * @param {string} endpoint - API endpoint
   * @param {string} method - HTTP method (POST, PUT, etc.)
   * @param {Object} jsonData - Raw JSON data for the request body
   * @param {Object} options - Additional axios options
   * @returns {Promise<any>} - Response data
   */
  async requestWithRawJson(endpoint, method, jsonData, options = {}) {
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers
    };

    return this.request(endpoint, method, jsonData, {
      ...options,
      headers
    });
  }

  /**
   * Make a request with form data in the request body
   * @param {string} endpoint - API endpoint
   * @param {string} method - HTTP method (POST, PUT, etc.)
   * @param {Object} formData - Form data as key-value pairs or FormData instance
   * @param {Object} options - Additional axios options
   * @returns {Promise<any>} - Response data
   */
  async requestWithFormData(endpoint, method, formData, options = {}) {
    // Create a new FormData instance
    let formDataToSend;
    
    // If it's already a FormData instance, use it directly
    if (formData instanceof FormData) {
      formDataToSend = formData;
    } 
    // If formData is a plain object, create a new FormData and append its properties
    else if (formData && typeof formData === 'object') {
      formDataToSend = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        // Handle null/undefined values
        if (value !== null && value !== undefined) {
          // Handle file objects (React Native)
          if (typeof value === 'object' && value.uri) {
            formDataToSend.append(key, {
              uri: value.uri,
              type: value.type || 'application/octet-stream',
              name: value.name || 'file'
            });
          } 
          // Handle File objects (web)
          else if (value instanceof File) {
            formDataToSend.append(key, value);
          }
          // Handle arrays and objects (convert to JSON string)
          else if (typeof value === 'object') {
            formDataToSend.append(key, JSON.stringify(value));
          }
          // Handle all other values
          else {
            formDataToSend.append(key, value);
          }
        }
      });
    } 
    // If formData is something else, create an empty FormData
    else {
      formDataToSend = new FormData();
    }
  
    // Create headers, removing Content-Type to let the browser set it with the correct boundary
    const headers = {
      ...options.headers,
      // Remove Content-Type header to let the browser set it with the correct boundary
    };
  
    // Make the request with the form data
    return this.request(endpoint, method, formDataToSend, {
      ...options,
      headers,
    });
  }

  /**
   * Make a request with URL query parameters
   * @param {string} endpoint - API endpoint
   * @param {string} method - HTTP method
   * @param {Object} queryParams - Query parameters as key-value pairs
   * @param {Object} data - Request body data (optional)
   * @param {Object} options - Additional axios options
   * @returns {Promise<any>} - Response data
   */
  async requestWithParams(endpoint, method, queryParams = {}, data = null, options = {}) {
    // Create a params object for Axios
    const params = { ...(queryParams || {}) };
    
    // Make the request with params and data
    return this.request(endpoint, method, data, {
      ...options,
      params: {
        ...options.params,
        ...params
      }
    });
  }

  /**
   * Set the authentication token
   * @param {string} token - The authentication token
   */
  setAuthToken(token) {
    if (token) {
      // Store the token in AsyncStorage
      localStorage.setItem('@auth_token', token);
      this.clearAuthFailureState();
    } else {
      localStorage.removeItem('@auth_token');
    }
    
    // Also update the axios instance headers
    if (token) {
      axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${token}`;

    } else {
      delete axiosInstance.defaults.headers.common['Authorization'];
    }
  }

  /**
   * Get the base URL of the API
   * @returns {string} The base URL
   */
  getBaseUrl() {
    return API_URL;
  }
  
  /**
   * Cancel all pending requests
   */
  cancelAllRequests() {
    this.abortControllers.forEach((controller) => {
      try {
        controller.abort();
      } catch (e) {
      }
    });
    this.abortControllers.clear();
    this.inFlightGetRequests.clear();
  }

  /**
   * Parse JWT token to extract expiry date
   * @param {string} token - JWT token
   * @returns {Date|null} - Expiry date or null
   */
  parseTokenExpiry(token) {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64).split('').map(c => {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join('')
      );
      const payload = JSON.parse(jsonPayload);
      if (payload.exp) {
        return new Date(payload.exp * 1000);
      }
      return null;
    } catch (error) {
      console.error('Error parsing JWT token:', error);
      return null;
    }
  }

  /**
   * Proactively refresh token before it expires
   * @param {string} refreshToken - Refresh token
   * @returns {Promise<string|null>} - New token or null
   */
  async proactiveTokenRefresh(refreshToken) {
    if (this.isRefreshing) {
      return null;
    }

    this.isRefreshing = true;

    try {
      const refreshResponse = await axiosInstance.post(
        '/auth/refresh-token',
        {},
        {
          headers: {
            Authorization: `Bearer ${refreshToken}`,
          },
        }
      );

      const refreshData = refreshResponse.data;
      const newToken = refreshData?.data?.accessToken ?? refreshData?.data?.token;
      const newRefreshToken = refreshData?.data?.refreshToken || refreshToken;

      if (!newToken) {
        throw new Error('No token returned from refresh endpoint');
      }

      // Parse JWT to get expiry
      const tokenExpiry = this.parseTokenExpiry(newToken);

      // Persist new tokens
      localStorage.setItem('@auth_token', newToken);
      localStorage.setItem('@refresh_token', newRefreshToken);
      if (tokenExpiry) {
        localStorage.setItem('@token_expiry', tokenExpiry.toISOString());
      }
      this.setAuthToken(newToken);

      this.isRefreshing = false;
      return newToken;
    } catch (error) {
      this.isRefreshing = false;
      console.error('Proactive token refresh failed:', error);
      return null;
    }
  }
}

// Create and export a singleton instance
const apiService = new APIService();
export default apiService;
